import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";
import CustomSelect from "./ReactSelect";
import SortableMultiValue from "./SortableMultiValue";
import Popout from "./Popout";
import { useState } from "react";
import { isEmpty } from "@/lib/filters/funcs";
const NO_OPTIONS_MESSAGE = "No Matches";
const DEFAULT_PLACEHOLDER = "Type to search...";

const ListIcon = () => <i className="fas fa-list" />;

export const OptionComponentWithIcon = (props) => (
  <components.Option {...props} className="d-flex justify-content-between">
    <span className={props.data.isActive === false ? "text-muted" : null}>
      {props.data.label}
    </span>
    <span>{props.data?.icon}</span>
  </components.Option>
);

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ListIcon />
  </components.DropdownIndicator>
);

const surpressEnterTab = (e) =>
  [13, 9].includes(e.keyCode) && e.preventDefault();

export const defaultStyles = {
  control: (base, state) => {
    const isInvalid = state.selectProps.className === "is-invalid";
    const isFocused = state.isFocused;
    const isDisabled = state.isDisabled;
    let color = isInvalid ? "#dc3545" : "#dcdcdc";
    if (isFocused) {
      color = isInvalid ? "#dc3545" : "#80bdff";
    }
    const boxShadow = isInvalid
      ? "0 0 0 0.2rem rgb(246, 204, 208)"
      : "0 0 0 0.2rem rgba(0, 123, 255, 0.25)";
    return {
      ...base,
      backgroundColor: isFocused ? "#fff" : isDisabled ? "#e9ecef" : "#fafafa",
      minHeight: "34px",
      height: state.isMulti ? "100%" : "34px",
      borderColor: color,
      boxShadow: isFocused ? boxShadow : "unset",
      "&:hover": {
        borderColor: color
      }
    };
  },
  valueContainer: (base) => ({
    ...base,
    cursor: "text",
    padding: "0px 5px"
  }),
  indicatorsContainer: (base, state) => ({
    ...base,
    height: state.isMulti ? "unset" : "34px"
  }),
  input: (base) => ({
    ...base,
    margin: "0px 2px",
    padding: 0,
    height: "34px"
  }),
  multiValueRemove: (base) => ({
    ...base,
    cursor: "pointer"
  }),
  multiValue: (base, { data }) => ({
    ...base,
    paddingRight: data.isProtected ? "3px" : "" // multiValueRemove component is removed, so we use the space
  })
};

const autoCompleteFilterStyles = {
  control: (base, state) => ({
    ...base,
    ...defaultStyles.control(base, state),
    backgroundColor: "#fff",
    width: 170,
    "@media (max-width: 576px)": {
      width: "100%"
    }
  }),
  menu: (base) => ({
    ...base,
    width: "max-content",
    minWidth: "100%"
  })
};

const getIsClearable = (value) => {
  if (!Array.isArray(value)) {
    return true;
  }
  const hasProtectedValue = value.some(
    (multiValue) => multiValue.isProtected === true
  );

  return !hasProtectedValue;
};

const getIsBackspaceClearable = (value) => {
  if (!Array.isArray(value)) {
    return true;
  }
  return value?.slice(-1)[0]?.isProtected !== true;
};

const AutocompleteInput = ({
  input,
  items,
  fieldApiParams,
  showValue,
  getShowValue,
  mapResponse,
  disabled,
  meta: { touched, error },
  placeholder,
  autocompleteInputConfig: {
    resetOnApiParamsChange,
    customStyles,
    isQuickAdd = false,
    customOptionComponent,
    menuPlacement,
    isAutoCompleteFilter,
    isSearchable = true,
    canHoldProtectedValues
  } = {},
  fieldRef,
  isMulti = false,
  isSortable = false,
  setFieldValue,
  filterOption,
  hasCreateButton,
  createButtonOptions,
  additionalFilters,
  values
}) => {
  const [additionalFiltersValues, setAdditionalFiltersValues] = useState();

  const componentsQuickAdd = {
    Option: customOptionComponent,
    IndicatorSeparator: null,
    DropdownIndicator: null
  };

  let styles = customStyles
    ? { ...defaultStyles, ...customStyles }
    : defaultStyles;

  if (isAutoCompleteFilter) {
    styles = { ...defaultStyles, ...autoCompleteFilterStyles };
  }

  let defaultComponents = {
    DropdownIndicator
  };

  if (isSortable && isMulti) {
    defaultComponents = {
      ...defaultComponents,
      MultiValue: SortableMultiValue
    };
  }

  if (canHoldProtectedValues) {
    defaultComponents = {
      ...defaultComponents,
      MultiValueRemove: (props) => {
        if (props.data.isProtected === true) return null;
        return <components.MultiValueRemove {...props} />;
      }
    };
  }

  if (customOptionComponent) {
    defaultComponents = { ...defaultComponents, Option: customOptionComponent };
  }

  if (additionalFilters) {
    const { categories, channels } = additionalFiltersValues || {};
    fieldApiParams = {
      ...fieldApiParams,
      apiParams: {
        ...fieldApiParams.apiParams,
        ...(!isEmpty(categories) && { categories: categories.value }),
        ...(!isEmpty(channels) && { channels: channels.value })
      }
    };
  }

  return (
    <CustomSelect
      key={
        additionalFilters || isQuickAdd || resetOnApiParamsChange
          ? JSON.stringify({ fieldApiParams })
          : undefined
      } // we need to re-render component to reload with new api params if changed
      fieldApiParams={fieldApiParams}
      items={items}
      input={input}
      isMulti={isMulti}
      showValue={getShowValue ? getShowValue(input.value) : showValue}
      mapResponse={mapResponse}
      isQuickAdd={isQuickAdd}
      setFieldValue={setFieldValue}
      isAutoCompleteFilter={isAutoCompleteFilter}
      hasCreateButton={hasCreateButton}
      createButtonOptions={createButtonOptions}
      isDisabled={disabled}
      values={values}
    >
      {({
        onChange,
        loadOptions,
        debounceTimeout,
        value,
        onInputChange,
        inputValue,
        sortMultiValues
      }) => (
        <>
          <AsyncPaginate
            filterOption={filterOption}
            className={error && touched ? "is-invalid" : ""}
            styles={styles}
            components={isQuickAdd ? componentsQuickAdd : defaultComponents}
            menuShouldScrollIntoView={false}
            selectRef={fieldRef}
            isClearable={canHoldProtectedValues ? getIsClearable(value) : true}
            backspaceRemovesValue={
              canHoldProtectedValues ? getIsBackspaceClearable(value) : true
            }
            isMulti={isMulti}
            isSearchable={isSearchable}
            openMenuOnClick={false}
            menuPosition={isQuickAdd ? null : "fixed"}
            menuPlacement={menuPlacement || "auto"}
            placeholder={placeholder || DEFAULT_PLACEHOLDER}
            value={value}
            loadOptions={loadOptions}
            onChange={onChange}
            defaultOptions={[]}
            isDisabled={disabled}
            isOptionSelected
            debounceTimeout={debounceTimeout}
            noOptionsMessage={() => NO_OPTIONS_MESSAGE}
            onBlur={() => input.onBlur(input.value)}
            menuIsOpen={isQuickAdd ? true : null}
            onInputChange={isQuickAdd ? onInputChange : null}
            inputValue={isQuickAdd ? inputValue : null}
            onKeyDown={isQuickAdd ? surpressEnterTab : null}
            sortMultiValues={sortMultiValues}
            fieldName={input.name}
            additional={{
              page: 1
            }}
          />
          {!isEmpty(additionalFilters) && (
            <div className="d-flex flex-row">
              {additionalFilters?.map((filter) => {
                return (
                  <Popout
                    key={filter.label}
                    {...filter}
                    value={additionalFiltersValues?.[filter?.resource]}
                    onValueChange={(selectedOption) => {
                      if (input.value) {
                        input.onChange("");
                      }
                      setAdditionalFiltersValues((prev) => ({
                        ...prev,
                        [filter.resource]: selectedOption || null
                      }));
                    }}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </CustomSelect>
  );
};

export default AutocompleteInput;
